import React, { useEffect, useState, useCallback } from 'react';
import { checkTokenStatus, connectAccount, removeToken } from '../api/authService';

const MainPage: React.FC = () => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [username, setUsername] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { isConnected: connected, username: user } = await checkTokenStatus();
      setIsConnected(connected);
      setUsername(user);
    } catch (error) {
      console.error('Error checking token status:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleConnect = () => {
    connectAccount(loadStatus);
  };

  const handleRemove = async () => {
    try {
      await removeToken();
      setIsConnected(false);
      setUsername(null);
    } catch (error) {
      console.error('Error removing token:', error);
    } finally {
      setConfirmRemove(false);
    }
  };

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-brand">
          <div className="header-logo">OL</div>
          <div>
            <h1 className="header-title">Outlook Connections</h1>
            <p className="header-subtitle">Manage your Outlook OAuth integrations</p>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Stats */}
        {!isLoading && isConnected && (
          <div className="stats-bar">
            <div className="stat-chip">
              <span className="stat-dot blue" />
              <div>
                <div className="stat-value">1</div>
                <div className="stat-label">Active</div>
              </div>
            </div>
            <div className="stat-chip">
              <div>
                <div className="stat-value">1</div>
                <div className="stat-label">Total</div>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <span>Loading connections...</span>
          </div>
        ) : !isConnected ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <rect width="64" height="64" rx="16" fill="#eff6ff" />
                <path d="M20 32h24M32 20v24" stroke="#0078d4" strokeWidth="3" strokeLinecap="round" />
                <rect x="14" y="14" width="36" height="36" rx="8" stroke="#0078d4" strokeWidth="2" strokeDasharray="4 3" />
              </svg>
            </div>
            <h2 className="empty-title">No Connections Yet</h2>
            <p className="empty-desc">Connect a Microsoft account to get started. Your Outlook integration will appear here.</p>
            <button className="btn-empty-add" onClick={handleConnect}>
              + Add Your First Connection
            </button>
          </div>
        ) : (
          <div className="connections-grid">
            <div className="connection-card">
              <div className="card-top">
                <div className="card-icon">OL</div>
                <span className="status-badge active">
                  <span className="status-dot" />
                  Active
                </span>
              </div>

              <h3 className="connection-name">Microsoft Account</h3>
              {username && <p className="connection-email">{username}</p>}

              <div className="card-actions">
                <button className="btn-refresh" onClick={handleConnect}>
                  Refresh
                </button>
                <button className="btn-remove" onClick={() => setConfirmRemove(true)}>
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Remove Modal */}
      {confirmRemove && (
        <div className="modal-overlay" onClick={() => setConfirmRemove(false)}>
          <div className="modal-dialog" onClick={e => e.stopPropagation()}>
            <div className="modal-icon">&#9888;</div>
            <h3 className="modal-title">Remove Connection</h3>
            <p className="modal-desc">
              Remove <strong>{username ?? 'Microsoft Account'}</strong>? This deletes the stored OAuth tokens and cannot be undone.
            </p>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setConfirmRemove(false)}>
                Cancel
              </button>
              <button className="btn-modal-confirm" onClick={handleRemove}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainPage;
